/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect, useState, useContext,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/th';
import { maxBy, minBy } from 'lodash';

import { useTranslation } from 'react-i18next';

// ui
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import NativeSelect from '@material-ui/core/NativeSelect';
import Select from '@material-ui/core/Select';

import DateFnsUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';

// HighCharts
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExport from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

// Provider
import UserContext from '../../../context/UserContext';

// init highcharts module
HighchartsExport(Highcharts);
HighchartsExportData(Highcharts);

export default function WaterVolumeChart(props) {
  const { t, i18n } = useTranslation();
  const { id, provinceId } = props;
  const today = moment(new Date()).locale('th').format('YYYY-MM-DD');

  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(true);

  const [teleStation, setTeleStation] = useState([]);
  const [canalStation, setCanalStation] = useState([]);
  const [dataProvince, setDataProvince] = useState([]);
  const [wlId, setWlId] = useState(id);
  const [stateProvinceId, setStateProvinceId] = useState(provinceId);
  const [startDate, setStartDate] = useState(moment().subtract(3, 'days').locale('th').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment(new Date()).locale('th').format('YYYY-MM-DD HH:mm'));
  const [chartOptions, setChartOptions] = useState();
  const [getMax, setMax] = useState();
  const { userIsLogin } = useContext(UserContext);

  const getChartOptions = () => {
    const options = {
      chart: {
        style: {
          fontFamily: 'kanit',
        },
      },
      title: {
        text: '',
      },
      plotOptions: {
        line: {
          turboThreshold: 0,
        },
      },
      annotations: [
        {
          labels: [
            {
              point: { x: 5, y: 5 },
              useHTML: true,
              text: `&#x25B2;  ${t('จุดที่มีปริมาณน้ำสูงสุด')}/${t('ต่ำสุด')}`,
            },
          ],
        },
      ],
      xAxis: {
        type: 'datetime',
        labels: {
          format: '{value:%d-%b<br />%H:%M}',
        },
      },
      yAxis: {
        // Primary yAxis
        title: {
          text: `${t('ปริมาณน้ำ')} (${t('ม.3/วิ')})`,
        },
        labels: {
          format: '{value:.2f}',
        },
        minorGridLineWidth: 0,
        gridLineWidth: 0,
        alternateGridColor: null,
        plotLines: [
          {
            value: null,
            color: 'red',
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'right',
            },
          },
        ],
      },
      legend: {
        enabled: false,
      },
      series: [
        {
          name: t('ปริมาณน้ำ'),
          data: [],
          tooltip: {
            valueSuffix: ` ${t('ม.3/วิ')}`,
          },
          dataLabels: {
            enabled: false,
            format: '{point.y:,.0f}',
          },
        },
      ],
      exporting: {
        buttons: {
          contextButton: {
            enabled: userIsLogin,
            menuItems: [
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
    };

    return options;
  };

  function getStation() {
    fetch(`${process.env.REACT_APP_API_URL}/frontend/shared/tele_canal_station?province_code=${stateProvinceId}`)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Something went wrong ...');
      })
      .then((result) => {
        setCanalStation(result.data.canal_waterlevel);
        setTeleStation(result.data.tele_waterlevel);
        let stationIDExist = false;
        result.data.tele_waterlevel.forEach((element) => {
          if (element.station_id === wlId) {
            stationIDExist = true;
          }
        });
        result.data.canal_waterlevel.forEach((element) => {
          if (element.station_id === wlId) {
            stationIDExist = true;
          }
        });
        if (!stationIDExist) {
          setWlId(result.data.tele_waterlevel[0].station_id);
        }
      })
      .catch((err) => setError(err));
  }

  function renderChart() {
    fetch(`${process.env.REACT_APP_API_URL}/public/waterlevel_graph?station_type=tele_waterlevel&station_id=${wlId}&start_date=${startDate}&end_date=${endDate}`)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        return 'Something went wrong.';
      })
      .then((result) => {
        const dataSeries = [];
        let ts = null;
        let val = null;

        result.data.graph_data.map((n) => {
          if (!Number.isNaN(parseFloat(n.discharge))) {
            val = parseFloat(parseFloat(n.discharge).toFixed(2));
          } else {
            val = null;
          }
          ts = moment(n.datetime, 'YYYY/MM/DD hh:mm:ss').add(7, 'hours').valueOf();
          dataSeries.push([ts, val]);

          return dataSeries;
        });
        // filter null and undefined
        const filteredSeries = dataSeries.filter((e) => e[1] !== null && e[1] !== undefined);
        let max;
        let min;
        if (filteredSeries.length === 0) {
          max = 0;
          min = 0;
        } else {
          max = maxBy(result.data.graph_data, 'discharge');
          min = minBy(result.data.graph_data, 'discharge');
        }
        setMax(max);

        // set chart options
        const chart = getChartOptions();
        const maxValue = maxBy([{ value: max.discharge }, { value: result.data.qmax }], 'value');

        chart.yAxis.max = maxValue.value + (0.02 * maxValue.value);
        // chart.yAxis.min = min.discharge - (0.02 * min.discharge);
        chart.yAxis.min = 0;

        chart.series[0].data = filteredSeries;

        // plotLines
        if (result.data.qmax !== null && !result.data.qmax <= 0) {
          chart.yAxis.plotLines[0].value = result.data.qmax;
          chart.yAxis.plotLines[0].label.text = `${t('ความจุลำน้ำ')} <strong>${parseFloat(result.data.qmax).toFixed(2)}</strong> ${t('ม.3/วิ.')}`;
        }

        setChartOptions(chart);
      })
      .catch((err) => setError(err));
  }

  useEffect(() => {
    const fetchData = async () => {
      fetch(`${process.env.REACT_APP_API_URL}/public/waterlevel_load`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Something went wrong ...');
        })
        .then((result) => {
          const province = result.province.data.sort((a, b) => a.province_name.th.localeCompare(b.province_name.th, 'th'));
          setDataProvince(province);
        })
        .catch((err) => setError(err));

      getStation();
      renderChart();
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    getStation();
  }, [stateProvinceId]);

  useEffect(() => {
    renderChart();
  }, [wlId, endDate]);

  const handleProvinceChange = (e) => {
    setStateProvinceId(e.target.value);
  };

  const handleStationChange = (event) => {
    setWlId(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleEndDateChange = (date) => {
    setEndDate(moment(date).format('YYYY-MM-DD HH:mm'));
  };

  return (
    <>
      {error && <div>Error Occured</div>}
      {isLoading && (
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} size="3x" />
        </div>
      )}
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} md={9}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={3} md={3}>
              <FormControl>
                <InputLabel shrink htmlFor="province-label-placeholder">
                  {t('จังหวัด')}
                </InputLabel>
                <Select id="wl-select-province" value={stateProvinceId} onChange={(e) => handleProvinceChange(e)}>
                  {dataProvince.map((row) => (
                    <MenuItem key={row.province_code} value={row.province_code}>
                      {i18n.language === 'en' ? row.province_name.en : row.province_name.th}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={9} md={9}>
              <FormControl>
                <InputLabel shrink htmlFor="station-label-placeholder">
                  {t('สถานี')}
                </InputLabel>
                <NativeSelect id="wl-select-id" value={wlId} onChange={handleStationChange}>
                  <optgroup label={t('โทรมาตร')}>
                    {teleStation.map((row) => (
                      <option key={row.station_id} value={row.station_id}>
                        {row.station_old_code}
                        {' '}
                        {row.station_name.th}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t('คลอง')}>
                    {canalStation.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.station_name.th}
                      </option>
                    ))}
                  </optgroup>
                </NativeSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <FormControl>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    id="wl-start-date"
                    label={t('วันที่เริ่มต้น')}
                    format="YYYY-MM-DD"
                    value={startDate}
                    maxDate={today}
                    onChange={handleStartDateChange}
                  />
                </MuiPickersUtilsProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <FormControl>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    id="wl-end-date"
                    label={t('วันที่สิ้นสุด')}
                    format="YYYY-MM-DD"
                    value={endDate}
                    maxDate={today}
                    onChange={handleEndDateChange}
                  />
                </MuiPickersUtilsProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <FormControl>
                <Button
                  id="wl-chart-btn"
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={renderChart}
                  title={t('แสดงกราฟ')}
                >
                  {t('แสดง')}
                </Button>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {
            getMax === 0 ? (
              <Paper>{t('ไม่มีข้อมูลในช่วงเวลาที่เลือก')}</Paper>
            ) : (
              <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            )
          }
        </Grid>
      </Grid>
    </>
  );
}

WaterVolumeChart.propTypes = {
  id: PropTypes.number.isRequired,
  provinceId: PropTypes.string.isRequired,
};
